import { useEffect, useState, useContext } from 'react'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import { Organization } from '@yuno/types'
import {
  countryNameFromCode,
  Input,
  Grid,
  MenuItem,
  Select,
  Typography,
  Divider,
  palette,
  RadioGroup,
  FormControlLabel,
  Icon,
} from '@yuno/design-system-web'
import { ConstantText } from '../../utils/constants'
import { useConnectionFieldRules } from '../../hooks/connection.hook'
import { IConnections, ITestKey } from '../../interfaces/connection.interfaces'
import { literals, mercadoPago } from './constants'
import { storage } from '@yuno/dashboard-container-mfe'
import { FormContext } from '@yuno/dashboard-container-mfe'
import {
  ChecboxContainer,
  CheckboxStyled,
  Condition,
  FormControlLabelStyled,
  Header,
  InputAvailableFields,
  InputContainer,
  InputLabelStyled,
  PaymentMethodsContainer,
  RadioButtonContainer,
  RadioMuiStyled,
  Title,
} from './styles'
import { ConnectionPagseguro } from '../connection-pagseguro'
import { useTestKeys } from '../../hooks/key-test.hook'
import { IconKeyTestOptions } from './connection-icon-test-keys'
import { ConnectionIntegrationType } from '../connection-integration-types'
import { ConnectionMercadoPago } from '../connection-mercado-pago'
import { useTranslation } from 'react-i18next'

export type ConnectionFormOptionalFields = {
  integration_type?: string[]
  panel_name: string
  panel_description: string
  param_id: string
  is_obfuscated?: boolean
  value?: string
}

export type IContext = {
  formData: any
  handleFormChange: any
}
export type ConnectionFormProps = {
  connectionName?: string
  name?: string
  connectionResponse?: Organization.IntegrationParams
  spacing?: number
  onSubmit: (any) => void
  fields?: ConnectionFormOptionalFields[]
  countries: string | string[]
  status?: string
  setFormValid?: (isValid: boolean) => void
  providerId?: string
  paymentMethods?: Organization.PaymentMethods[]
  data?: IConnections
  error?: string
  handleChangeCountry?: (event) => void
  testKeyValues?: ITestKey[]
  currentMode?: 'edit' | 'create'
  integrationTypes?: any
  isAsync?: boolean
  handleHasChange?: (value: boolean) => void
}

const initDefaultValues = (arr, connectionResponse, formData) => {
  let initialValues = connectionResponse?.params
  const hasTestCredentials = connectionResponse?.test_credentials
  const obj = {}
  if (arr) {
    for (let i = 0; i < arr.length; i++) {
      const { param_id } = arr[i]
      obj[param_id] =
        param_id === mercadoPago.guestModeKeyParam
          ? formData[param_id] ?? 'true'
          : ''
      if (formData[param_id]) {
        obj[param_id] = formData[param_id]
      } else if (initialValues) {
        obj[param_id] =
          initialValues.find((e) => e.param_id === param_id)?.value ??
          (hasTestCredentials ? 'input_test_value' : '')
      }
    }
  }
  return obj
}

export const ConnectionForm = ({
  spacing = 2.5,
  onSubmit,
  fields,
  paymentMethods,
  countries,
  status,
  setFormValid,
  providerId,
  error,
  connectionResponse,
  connectionName,
  handleChangeCountry,
  testKeyValues = [],
  currentMode = 'create',
  integrationTypes,
  isAsync,
  handleHasChange,
}: ConnectionFormProps) => {
  const [countrySelected, setCountrySelected] = useState('')
  const [hasSelectFocus, setHasSelectFocus] = useState({
    country: false,
    paymentMethod: false,
    integrationType: false,
  })

  const [methodsEdited, setMethodsEdited] = useState(false)
  const {
    enabledPaymentMethods,
    enabledPaymentMethodsKeyParam,
    guestModeKeyParam,
    mercadoPagoProMethod,
    paymentMethodDisabled,
  } = mercadoPago
  const {
    mercadoPago: {
      enabledPaymentMethodsLabel,
      guestModeLabel,
      raddioButtonNo,
      raddioButtonYes,
    },
  } = literals
  const { formData, handleFormChange } = useContext<IContext>(FormContext)
  const payment_method = connectionResponse?.payment_method ?? null
  const integration_type = connectionResponse?.integration_type ?? null
  const handleChange = (e) => {
    setValue('country', e.target.value)
    handleChangeCountry(e)
    if (providerId === 'PAGSEGURO' || providerId === 'PAYMENTEZ') {
      setValue('payment_method_type', null)
    }
    resetTestKeyProps()
  }
  const { t } = useTranslation()

  const setCountry = () =>
    !Array.isArray(countries)
      ? countries
      : countries
      ? connectionResponse?.country ?? ''
      : 'GLOBAL'

  const setPaymentMethod = () => {
    if (integration_type && providerId === 'MERCADO_PAGO') {
      return getValues('payment_method_type')
        ? getValues('payment_method_type')
        : payment_method
        ? payment_method[0]
        : null
    }
    if (providerId === 'TRANSBANK_2') {
      if (integration_type === ConstantText.WEB_PAY_PLUS) {
        return 'WEBPAY_PLUS'
      }
      if (integration_type === ConstantText.WEB_PAY_PLUS_MALL) {
        return 'WEBPAY_PLUS_MALL'
      }
    }
    return integration_type === ConstantText.WEB_PAY_PLUS ? 'WEBPAY' : 'CARD'
  }

  const getPaymentMethod = () => {
    if (integration_type && providerId === 'MERCADO_PAGO') {
      return payment_method ? payment_method[0] : null
    }
    return !integration_type
      ? formData['payment_method_type'] ??
          (payment_method && (payment_method[0] ?? null))
      : setPaymentMethod()
  }
  const defaultValues: {
    country?: string
    payment_method_type: string
    name?: string
    params_with_test_values: boolean
    integration_type?: string
  } = {
    ...initDefaultValues(fields, connectionResponse, formData),
    country: formData['country'] ?? setCountry(),
    integration_type: integration_type ?? null,
    payment_method_type: getPaymentMethod(),
    name: formData['name'] ?? connectionName,
    params_with_test_values: formData['params_with_test_values'] ?? false,
    //integration_type: formData['integration_type'] ?? integration_type ?? null,
  }

  const methods = useForm({
    mode: 'onChange',
    defaultValues,
  })

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, isValid, isDirty },
    reset,
    setError,
    control,
    watch,
    resetField,
    trigger,
  } = methods

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      handleFormChange(name, value[name])
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const paymentMethodEdit = payment_method
    ? getValues('payment_method_type') ?? payment_method[0]
    : null
  const paymentMethodCreate =
    paymentMethods && getValues('payment_method_type')
      ? getValues('payment_method_type') ?? paymentMethods[0]
      : null

  const selectedMethod = connectionResponse
    ? paymentMethodEdit
    : paymentMethodCreate

  const { availableFields } = useConnectionFieldRules({
    providerId,
    fields,
    selectedMethod: integration_type ? setPaymentMethod() : selectedMethod,
    integrationType: getValues('integration_type') ?? integration_type,
  })

  useEffect(() => {
    setFormValid &&
      setFormValid(connectionResponse && !isValid ? methodsEdited : isValid)

    if (currentMode === 'create') {
      handleHasChange(isValid)
    } else {
      handleHasChange(isValid && isDirty)
    }
  }, [isValid, isDirty, methodsEdited])

  useEffect(() => {
    if (status === 'idle') {
      reset()
    }
    if (status == 'error' && error == ConstantText.ERROR_MESSAGE_NAME) {
      setError(
        'name',
        { message: t('nameAlreadyExist') },
        { shouldFocus: true },
      )
    }
  }, [status, error])

  const handleFocus = (field: string, focused: boolean) => {
    setHasSelectFocus((prev) => ({ ...prev, [field]: focused }))
  }

  const handleChangePayment = (e) => {
    setValue('payment_method_type', e.target.value)
    resetTestKeyProps()
  }
  const handleChangeIntegration = (e) => {
    setValue('integration_type', e.target.value.id)
    const paymentMethod = integrationTypes.filter(
      (element) => element.id === e.target.value.id,
    )
    setValue('payment_method_type', paymentMethod[0].payment_method)
    resetTestKeyProps()
  }

  const validParameters = (el) =>
    !(
      el === 'country' ||
      el === 'name' ||
      el === 'payment_method_type' ||
      el === 'integration_type'
    )

  const mercadoPagoParams = (param, value) => {
    switch (param) {
      case enabledPaymentMethodsKeyParam:
        let enableMethodsOnlyValues = enabledPaymentMethods.map(
          (item) => item.value,
        )
        const valueSelected = [...value, paymentMethodDisabled]
        valueSelected.forEach((item) => {
          enableMethodsOnlyValues = enableMethodsOnlyValues.filter(
            (paymentMethod) => paymentMethod !== item,
          )
        })

        let paramValue = ''
        enableMethodsOnlyValues.forEach((paymentMethod, index) => {
          paramValue = `${paramValue}${paymentMethod}${
            index + 1 !== enableMethodsOnlyValues.length ? ',' : ''
          }`
        })
        return paramValue
      case guestModeKeyParam:
        return value === 'true'
      default:
        return null
    }
  }

  const handleSubmitForm = (data) => {
    storage.setItem('tempValuesForm', data)
    if (data.country === '' || !data.country) {
      data.country = 'GLOBAL'
    }

    const paramsCreate = {
      provider_id: providerId,
      name: data.name?.trim(),
      payment_method_type: data.payment_method_type,
      country: data.country,
      params: Object.keys(data)
        .filter((el) => validParameters(el))
        .map((el) => {
          return {
            param_id: el,
            value:
              (el === enabledPaymentMethodsKeyParam ||
                el === guestModeKeyParam) &&
              selectedMethod === mercadoPagoProMethod
                ? mercadoPagoParams(el, data?.[el])
                : data?.[el],
          }
        }),
      params_with_test_values: data.params_with_test_values,
      integration_type: data.integration_type,
    }

    const paramsEdit = {
      provider_id: providerId,
      name: data.name?.trim(),
      country: data.country,
      params: availableFields.map((el) => {
        return {
          param_id: el.param_id,
          value:
            (el.param_id === enabledPaymentMethodsKeyParam ||
              el.param_id === guestModeKeyParam) &&
            selectedMethod === mercadoPagoProMethod
              ? mercadoPagoParams(el.param_id, data?.[el.param_id])
              : data?.[el.param_id],
        }
      }),
    }
    onSubmit(connectionResponse ? paramsEdit : paramsCreate)
  }

  const renderPaymentMethodType = (type: string) => {
    const value = type
      ? paymentMethods?.find((el) => el.type === type)?.name
      : connectionResponse?.payment_method
      ? paymentMethods?.find(
          (el) => el.type === connectionResponse.payment_method[0],
        )
        ? paymentMethods?.find(
            (el) => el.type === connectionResponse.payment_method[0],
          ).name
        : t('select')
      : t('select')
    return value
  }

  const renderCountryValue = (value) =>
    connectionResponse
      ? countryNameFromCode(connectionResponse?.country)
      : value
      ? countryNameFromCode(value)
      : t('select')

  const renderIntegrationType = (value) => {
    return value?.name ?? value ?? t('select')
  }

  const isValidProvider = () =>
    [
      'MERCADO_PAGO',
      'STP',
      'ARCUS',
      'TRANSBANK',
      'PAGSEGURO',
      'TRANSBANK_2',
    ].includes(providerId)

  useEffect(() => {
    setCountrySelected(setCountry)
  }, [defaultValues])

  const setCheckboxDefaultChecked = (
    isMercadoPagoPaymentMethod,
    paymentMethod,
  ): boolean => {
    const enabledPaymentMethods = connectionResponse
      ? connectionResponse['params']?.filter(
          (param) => param.param_id === enabledPaymentMethodsKeyParam,
        )
      : null

    if (
      isMercadoPagoPaymentMethod ||
      formData[`${mercadoPago.enabledPaymentMethodsKeyParam}`]?.includes(
        paymentMethod.value,
      )
    ) {
      return true
    }
    return enabledPaymentMethods?.length > 0
      ? !enabledPaymentMethods[0].value?.includes(paymentMethod.value)
      : false
  }

  useEffect(() => {
    const cacheValues = storage.getItem('tempValuesForm')
    cacheValues && reset({ ...cacheValues }, { keepErrors: true })
  }, [])

  const {
    hasTestKeysAvailable,
    isCreateMode,
    isTestValue,
    handleTestKeyButton,
    resetTestKeyProps,
  } = useTestKeys({
    testKeyValues,
    currentMode,
    availableFields,
    resetField,
    setValue,
    trigger,
  })

  return (
    <div>
      <FormProvider {...methods}>
        <form id="connection-form" onSubmit={handleSubmit(handleSubmitForm)}>
          <Grid container spacing={spacing}>
            {process.env.ENVIRONMENT !== '' && (
              <Grid item xs={12} sx={{ paddingTop: '20px' }}>
                {/* @ts-ignore */}
                <Input
                  id="name"
                  type={'text'}
                  label={t('name')}
                  placeholder={t('enterName')}
                  name={'name'}
                  {...register('name' as never, { required: true })}
                  fullWidth
                  error={!!errors['name']}
                  helperText={
                    errors['name'] && errors['name'].message
                      ? errors['name'].message
                      : errors['name']
                      ? t('fillField')
                      : ''
                  }
                />
              </Grid>
            )}
            {(connectionResponse && connectionResponse.country !== 'GLOBAL') ||
            connectionResponse === undefined ? (
              countries &&
              typeof countries !== 'string' && (
                <Grid item xs={12} sx={{ paddingTop: '20px' }}>
                  <InputLabelStyled
                    focused={hasSelectFocus.country}
                    id="country"
                  >
                    Country
                  </InputLabelStyled>
                  <Select
                    labelId="country"
                    id="country"
                    name="country"
                    defaultValue={formData['country']}
                    fullWidth
                    variant="outlined"
                    MenuProps={{
                      id: 'country-menu',
                      sx: { maxHeight: '300px' },
                    }}
                    error={!!errors['country']}
                    displayEmpty
                    renderValue={(value: string) => {
                      return renderCountryValue(value)
                    }}
                    {...register('country' as never, {
                      required: true,
                      disabled: !!connectionResponse,
                    })}
                    onBlur={() => handleFocus('country', false)}
                    onFocus={() => handleFocus('country', true)}
                    onChange={handleChange.bind(this)}
                  >
                    {countries
                      .sort()
                      ?.map((name) => (
                        <MenuItem key={name} value={name}>
                          {countryNameFromCode(name)}
                        </MenuItem>
                      ))
                      .sort()}
                  </Select>
                </Grid>
              )
            ) : (
              <></>
            )}

            {providerId === 'TRANSBANK_2' && (
              <ConnectionIntegrationType
                hasSelectFocus={hasSelectFocus}
                renderIntegrationType={renderIntegrationType}
                connectionResponse={connectionResponse}
                handleFocus={handleFocus}
                handleChangeIntegration={handleChangeIntegration}
                integrationTypes={integrationTypes}
                renderPaymentMethodType={renderPaymentMethodType}
                handleChangePayment={handleChangePayment}
                formData={formData}
                paymentMethods={paymentMethods}
              />
            )}

            {providerId === 'MERCADO_PAGO' && (
              <ConnectionMercadoPago
                hasSelectFocus={hasSelectFocus}
                renderIntegrationType={renderIntegrationType}
                connectionResponse={connectionResponse}
                handleFocus={handleFocus}
                handleChangeIntegration={handleChangeIntegration}
                integrationTypes={integrationTypes}
                renderPaymentMethodType={renderPaymentMethodType}
                handleChangePayment={handleChangePayment}
                formData={formData}
                paymentMethods={paymentMethods}
              />
            )}

            {providerId === 'PAGSEGURO' && getValues('country') && (
              <ConnectionPagseguro
                hasSelectFocus={hasSelectFocus}
                renderPaymentMethodType={renderPaymentMethodType}
                connectionResponse={connectionResponse}
                handleFocus={handleFocus}
                handleChangePayment={handleChangePayment}
                paymentMethods={paymentMethods}
                providerId={providerId}
                formData={formData}
              />
            )}

            {isValidProvider() &&
              providerId !== 'PAGSEGURO' &&
              providerId !== 'TRANSBANK_2' &&
              providerId !== 'MERCADO_PAGO' && (
                <Grid item xs={12} sx={{ paddingTop: '20px' }}>
                  <InputLabelStyled
                    focused={hasSelectFocus.paymentMethod}
                    id="payment_method_type"
                  >
                    Payment Method
                  </InputLabelStyled>
                  <Select
                    labelId="payment_method_type"
                    id="payment_method_type"
                    name="payment_method_type"
                    defaultValue={formData['payment_method_type']}
                    fullWidth
                    variant="outlined"
                    displayEmpty
                    MenuProps={{ id: 'payment_method_type-menu' }}
                    error={!!errors['payment_method_type']}
                    renderValue={renderPaymentMethodType}
                    {...register('payment_method_type' as never, {
                      required: true,
                      disabled: !!connectionResponse,
                    })}
                    onBlur={handleFocus.bind(null, 'paymentMethod', false)}
                    onFocus={handleFocus.bind(null, 'paymentMethod', true)}
                    onChange={handleChangePayment.bind(this)}
                  >
                    {paymentMethods?.map((item) => (
                      <MenuItem key={item.type} value={item.type}>
                        {item.description || item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              )}

            {(countrySelected || getValues('country')) &&
              providerId == 'PAYMENTEZ' &&
              paymentMethods?.length > 0 && (
                <Grid item xs={12} sx={{ paddingTop: '20px' }}>
                  <InputLabelStyled
                    focused={hasSelectFocus.paymentMethod}
                    id="payment_method_type"
                  >
                    Payment Method
                  </InputLabelStyled>
                  <Select
                    defaultValue={formData['payment_method_type']?.value}
                    labelId="payment_method_type"
                    id="payment_method_type"
                    name="payment_method_type"
                    fullWidth
                    variant="outlined"
                    displayEmpty
                    MenuProps={{ id: 'payment_method_type-menu' }}
                    error={!!errors['payment_method_type']}
                    renderValue={renderPaymentMethodType}
                    {...register('payment_method_type' as never, {
                      required: true,
                      disabled: !!connectionResponse,
                    })}
                    onBlur={() => handleFocus('paymentMethod', false)}
                    onFocus={() => handleFocus('paymentMethod', true)}
                  >
                    {paymentMethods?.map((item) => (
                      <MenuItem key={item.type} value={item.type}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              )}

            {availableFields?.map((field, key) => {
              const hasTestValue = isTestValue(getValues(field.param_id))
              const isAvailableTestKey = hasTestKeysAvailable(
                getValues('country'),
                getValues('payment_method_type'),
              )
              return (
                <Grid item xs={12} key={key} sx={{ paddingTop: '20px' }}>
                  {field?.param_id !== enabledPaymentMethodsKeyParam &&
                    field?.param_id !== guestModeKeyParam && (
                      <InputContainer>
                        <InputLabelStyled
                          id={field.param_id}
                          focused={hasSelectFocus[field.param_id]}
                        >
                          {field.panel_name}
                        </InputLabelStyled>
                        {/* @ts-ignore */}
                        <InputAvailableFields
                          key={`input.${key}`}
                          type={
                            field?.is_obfuscated || hasTestValue
                              ? 'password'
                              : 'text'
                          }
                          placeholder={field.panel_description}
                          name={`params.${field.param_id}`}
                          {...register(field.param_id as never, {
                            required: true,
                          })}
                          fullWidth
                          error={
                            !isAvailableTestKey && !!errors[field.param_id]
                          }
                          helperText={
                            isAvailableTestKey
                              ? ''
                              : errors[field.param_id]
                              ? t('fillField')
                              : ''
                          }
                          disabled={hasTestValue || isAsync}
                          onBlur={() => handleFocus(field.param_id, false)}
                          onFocus={() => handleFocus(field.param_id, true)}
                        />
                        {(!!isAvailableTestKey || hasTestValue) && (
                          <IconKeyTestOptions
                            key={`icon.${key}`}
                            isMainInput={key === 0}
                            hasTestValue={hasTestValue}
                            isCreateMode={isCreateMode}
                            handleTestKeyButton={handleTestKeyButton}
                          />
                        )}
                      </InputContainer>
                    )}
                  {key === 0 && selectedMethod === mercadoPagoProMethod && (
                    <>
                      <Header>
                        <Title>{t('configurations')}</Title>
                        <Condition>{t('required')}</Condition>
                      </Header>
                      <Divider sx={{ background: palette.grey[2] }} />
                    </>
                  )}
                  {field?.param_id === enabledPaymentMethodsKeyParam && (
                    <InputLabelStyled focused={false}>
                      {enabledPaymentMethodsLabel}
                    </InputLabelStyled>
                  )}
                  <PaymentMethodsContainer>
                    {field?.param_id === enabledPaymentMethodsKeyParam &&
                      enabledPaymentMethods.map((paymentMethod, index) => {
                        const isMercadoPagoPaymentMethod =
                          paymentMethod.value === paymentMethodDisabled
                        return (
                          <ChecboxContainer>
                            <FormControlLabel
                              key={index}
                              value={paymentMethod.value}
                              name={paymentMethod.value}
                              control={
                                <CheckboxStyled
                                  size="medium"
                                  {...register(
                                    enabledPaymentMethodsKeyParam as never,
                                  )}
                                  defaultChecked={setCheckboxDefaultChecked(
                                    isMercadoPagoPaymentMethod,
                                    paymentMethod,
                                  )}
                                  disabled={isMercadoPagoPaymentMethod}
                                  onClick={() =>
                                    setMethodsEdited(
                                      connectionResponse ? true : false,
                                    )
                                  }
                                />
                              }
                              label={
                                <Typography variant="caption">
                                  {paymentMethod.name}
                                </Typography>
                              }
                              labelPlacement="end"
                            />
                          </ChecboxContainer>
                        )
                      })}
                  </PaymentMethodsContainer>
                  {field?.param_id === guestModeKeyParam && (
                    <>
                      <Divider
                        sx={{
                          background: palette.grey[2],
                          marginBottom: '20px',
                        }}
                      />
                      <InputLabelStyled focused={false}>
                        {guestModeLabel}
                      </InputLabelStyled>
                      <RadioButtonContainer>
                        <Controller
                          rules={{ required: true }}
                          control={control}
                          name={field.param_id}
                          render={({ field }) => (
                            <RadioGroup
                              {...field}
                              defaultValue="true"
                              onClick={() =>
                                setMethodsEdited(
                                  connectionResponse ? true : false,
                                )
                              }
                            >
                              {[raddioButtonYes, raddioButtonNo].map(
                                (text, index) => (
                                  <FormControlLabelStyled
                                    key={index}
                                    value={index === 0 ? 'true' : 'false'}
                                    label={text}
                                    control={
                                      <RadioMuiStyled
                                        checkedIcon={
                                          <Icon
                                            name="CheckCircle"
                                            color={palette.vivid.brand}
                                            weight="fill"
                                            size={'16px'}
                                          />
                                        }
                                      />
                                    }
                                  />
                                ),
                              )}
                            </RadioGroup>
                          )}
                        />
                      </RadioButtonContainer>
                    </>
                  )}
                </Grid>
              )
            })}
          </Grid>
        </form>
      </FormProvider>
    </div>
  )
}
